import { type OperationVariables, useApolloClient } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import type { DocumentNode } from 'graphql';
import { useCallback } from 'react';

import { useReportError } from '@/hooks/useReportError';
import {
  FetchDispositiveProvisionSuggestionsV2Document,
  type FetchDispositiveProvisionSuggestionsV2QueryVariables,
} from '@/modules/aiDP/graphql/aiDPV2.generated';
import {
  FetchEntityAiSuggestionsV2Document,
  type FetchEntityAiSuggestionsV2QueryVariables,
} from '@/modules/aiSuggestions/graphql/aiSuggestionsV2.generated';
import {
  FetchTestamentaryEntityAiSuggestionsV2Document,
  type FetchTestamentaryEntityAiSuggestionsV2QueryVariables,
} from '@/modules/aiSuggestions/graphql/testamentaryEntitySuggestionsV2.generated';
import {
  ReviewSuggestionCountsDocument,
  type ReviewSuggestionCountsQueryVariables,
} from '@/modules/aiSuggestions/reviewSuggestions/ReviewSuggestions.generated';
import {
  FetchBeneficiaryDesignationSuggestionsDocument,
  type FetchBeneficiaryDesignationSuggestionsQueryVariables,
} from '@/modules/beneficiaryDesignations/graphql/BeneficiaryDesignationSuggestions.generated';
import {
  EstateOnboardingUnacknowledgedSuggestionsV2Document,
  type EstateOnboardingUnacknowledgedSuggestionsV2QueryVariables,
} from '@/modules/estate/estateAIOnboarding/graphql/EstateOnboardingUnacknowledgedSuggestionsV2.generated';
import {
  EstatePageDataDocument,
  type EstatePageDataQueryVariables,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { WorkflowTracker, WorkflowTrackerKind } from '@/types/schema';

import { useHouseholdWorkflowTrackersContext } from '../contexts/useHouseholdWorkflowTrackersContext';
import { useOnHouseholdWorkflowTrackerComplete } from './useOnHouseholdWorkflowTrackerComplete';

/** An operation name and the name of one of its variables. */
type OperationVariable = [operationName: string | null, variable: string];

/** Pairs a query with one of its variables. The variable name is type-checked. */
function operationVariable<TVariables>(
  document: DocumentNode,
  variable: keyof TVariables & string
): OperationVariable {
  return [getOperationName(document), variable];
}

/** Maps each query for one root's suggestions to the variable that holds the root ID. */
const targetIDVariableByOperation = new Map([
  operationVariable<FetchEntityAiSuggestionsV2QueryVariables>(
    FetchEntityAiSuggestionsV2Document,
    'entityID'
  ),
  operationVariable<FetchTestamentaryEntityAiSuggestionsV2QueryVariables>(
    FetchTestamentaryEntityAiSuggestionsV2Document,
    'testamentaryEntityID'
  ),
  operationVariable<FetchDispositiveProvisionSuggestionsV2QueryVariables>(
    FetchDispositiveProvisionSuggestionsV2Document,
    'targetId'
  ),
  operationVariable<FetchBeneficiaryDesignationSuggestionsQueryVariables>(
    FetchBeneficiaryDesignationSuggestionsDocument,
    'entityId'
  ),
]);

/**
 * Maps each query that combines suggestions for many roots to the variable
 * that holds the household ID.
 */
const householdIDVariableByOperation = new Map([
  operationVariable<EstatePageDataQueryVariables>(
    EstatePageDataDocument,
    'householdId'
  ),
  operationVariable<ReviewSuggestionCountsQueryVariables>(
    ReviewSuggestionCountsDocument,
    'householdId'
  ),
  operationVariable<EstateOnboardingUnacknowledgedSuggestionsV2QueryVariables>(
    EstateOnboardingUnacknowledgedSuggestionsV2Document,
    'householdID'
  ),
]);

/** Returns the IDs of the entities and testamentary entities that a tracker generates suggestions for. */
function getTargetIDs(tracker: WorkflowTracker | undefined): string[] {
  return [
    ...(tracker?.entities ?? []).map((entity) => entity.id),
    ...(tracker?.testamentaryEntities ?? []).map((entity) => entity.id),
  ];
}

/**
 * Returns true if a mounted query can show suggestions for one of `targetIDs`.
 * Target queries match when their root ID is in `targetIDs`. Household queries
 * match when their household ID is `householdID`, because they include every
 * root. All other queries do not match.
 */
function showsGeneratedSuggestions(
  operationName: string | null,
  variables: OperationVariables,
  targetIDs: ReadonlySet<string>,
  householdID: string
): boolean {
  const targetIDVariable = targetIDVariableByOperation.get(operationName);
  if (targetIDVariable) {
    return targetIDs.has(variables[targetIDVariable]);
  }
  const householdIDVariable = householdIDVariableByOperation.get(operationName);
  return (
    householdIDVariable !== undefined &&
    variables[householdIDVariable] === householdID
  );
}

/**
 * Refetches mounted queries when household workflow trackers complete.
 *
 * A household chat completion refetches every active query. A suggestion
 * generation completion refetches only the active queries that can show the
 * new suggestions: the target queries for the tracker's roots, and the
 * household queries. Household queries refetch even if no card for the root is
 * mounted.
 */
export function useRefetchActiveQueriesOnHouseholdWorkflowComplete() {
  const apolloClient = useApolloClient();
  const { reportError } = useReportError();
  const { byId, snapshotStatus } = useHouseholdWorkflowTrackersContext();

  const reportRefetchError = useCallback(
    (error: unknown) => {
      reportError(
        'Failed to refresh household data after workflow updates',
        error instanceof Error ? error : new Error(String(error))
      );
    },
    [reportError]
  );

  const refetchActiveQueries = useCallback(() => {
    // Refresh mounted Apollo query observers after workflow writes. Forms that copy
    // query results into local state must also reconcile that local snapshot.
    void apolloClient
      .refetchQueries({ include: 'active' })
      .catch(reportRefetchError);
  }, [apolloClient, reportRefetchError]);

  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.HouseholdChatInteraction,
    onCompleted: refetchActiveQueries,
  });

  const refetchSuggestionQueries = useCallback(
    (completedTrackerIDs: string[]) => {
      // The provider keeps only trackers for its own household. Use that
      // household because a tracker can have a null householdID.
      const householdID = snapshotStatus?.householdID;
      if (!householdID) return;
      const targetIDs = new Set(
        completedTrackerIDs.flatMap((id) => getTargetIDs(byId[id]))
      );
      void apolloClient
        .refetchQueries({
          include: 'active',
          onQueryUpdated: (query) =>
            showsGeneratedSuggestions(
              getOperationName(query.options.query),
              query.variables ?? {},
              targetIDs,
              householdID
            ),
        })
        .catch(reportRefetchError);
    },
    [apolloClient, byId, reportRefetchError, snapshotStatus?.householdID]
  );

  // These are the tracker kinds that the backend creates for suggestion
  // generation. The schema has other generation kinds that no workflow uses.
  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.AiSuggestionsV2EntityDetailsGeneration,
    onCompleted: refetchSuggestionQueries,
  });
  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.AiSuggestionsV2SubtrustDetailsGeneration,
    onCompleted: refetchSuggestionQueries,
  });
  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.AiSuggestionsV2DpGeneration,
    onCompleted: refetchSuggestionQueries,
  });
}
